
import { toast as showToast } from "@/hooks/use-toast";

/**
 * Validates if locations are selected
 */
export const validateLocations = (selectedLocations: any[]): boolean => {
  const isValid = selectedLocations && selectedLocations.length > 0;
  
  if (!isValid) {
    console.error('Payment validation failed: No locations selected');
  }
  
  return isValid;
};

/**
 * Validates if campaign duration is valid
 */
export const validateDuration = (duration: number): boolean => {
  const isValid = duration && duration > 0;
  
  if (!isValid) {
    console.error('Payment validation failed: Invalid duration', duration);
  }
  
  return isValid;
};

/**
 * Validates if creative assets exist
 */
export const validateCreatives = (): boolean => {
  const creativeUrls = localStorage.getItem('creativeUrls');
  const allCreativeUrls = localStorage.getItem('allCreativeUrls');
  const mediaOwnerCreativeUrls = localStorage.getItem('mediaOwnerCreativeUrls');
  
  const hasCreatives = 
    (creativeUrls && creativeUrls !== '[]') || 
    (allCreativeUrls && allCreativeUrls !== '[]') || 
    (mediaOwnerCreativeUrls && mediaOwnerCreativeUrls !== '[]');
  
  if (!hasCreatives) {
    console.error('Payment validation failed: No creative URLs found');
  }
  
  return hasCreatives;
};

/**
 * Validates if campaign name exists
 */
export const validateCampaignName = (): boolean => {
  const campaignName = localStorage.getItem('campaignName');
  const isValid = campaignName && campaignName.trim() !== '';
  
  if (!isValid) {
    console.error('Payment validation failed: No campaign name found');
  }
  
  return isValid;
};

/**
 * Validates if media owner creatives exist when needed
 */
export const validateMediaOwnerCreatives = (selectedLocations: any[]): boolean => {
  if (!selectedLocations || selectedLocations.length === 0) {
    return true; // Skip validation if no locations
  }
  
  // Extract unique media owners
  const mediaOwners = [...new Set(
    selectedLocations
      .filter(location => location && location.mediaOwner)
      .map(location => location.mediaOwner)
  )];
  
  // If we have media owners and no media owner creatives, check if we have general creatives
  if (mediaOwners.length > 0) {
    const mediaOwnerCreativeUrls = localStorage.getItem('mediaOwnerCreativeUrls');
    
    if (!mediaOwnerCreativeUrls || mediaOwnerCreativeUrls === '{}') {
      const creativeUrls = localStorage.getItem('creativeUrls');
      const isValid = creativeUrls && creativeUrls !== '[]';
      
      if (!isValid) {
        console.error('Payment validation failed: No media owner creatives found for:', mediaOwners);
      }
      
      return isValid;
    }
  }
  
  return true;
};

/**
 * Logs validation state for debugging
 */
export const logValidationState = (
  isValid: boolean, 
  selectedLocations: any[], 
  duration: number, 
  validationErrors: string[]
): void => {
  console.log('Payment validation state:', { 
    isValid, 
    locations: selectedLocations?.length, 
    duration,
    hasCreatives: validateCreatives(),
    hasCampaignName: validateCampaignName(),
    validationErrors
  });
};

/**
 * Shows validation error toast and optionally resets to first step
 */
export const handleValidationFailure = (
  validationErrors: string[],
  t: (key: string) => string,
  toast: typeof showToast,
  onResetToFirstStep?: () => void
): void => {
  if (validationErrors.length > 0) {
    console.log('Validation failed with errors:', validationErrors);
    
    // Show validation error toast with proper error messages
    toast({
      title: t("error") || "Error",
      description: t("campaignValidationFailed") || "Please complete all previous steps before proceeding to payment.",
      variant: "destructive",
    });
    
    // Reset to first step only if explicitly requested
    if (onResetToFirstStep) {
      onResetToFirstStep();
    }
  }
};
